import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface DocumentIntrare {
  id: string;
  nrInregistrare: string;
  dataInregistrare: {
    anul: number;
    luna: number;
    ziua: number;
  };
  nrSiAdresaDocument: string;
  emitent: string;
  continutDocument: string;
  compartimentPrimeste: string;
  status: 'inregistrat' | 'distribuit' | 'arhivat';
  prioritate: 'normal' | 'urgent' | 'foarte_urgent';
  parohieId?: string;
}

interface DocumentIesire {
  id: string;
  dataExpediere: {
    anul: number;
    luna: number;
    ziua: number;
  };
  destinatar: string;
  continutDocument: string;
  nrInregistrareAnexa: string;
  indicativDosar: string;
  status: 'pregatit' | 'expediat' | 'confirmat';
  prioritate: 'normal' | 'urgent' | 'foarte_urgent';
  parohieId?: string;
}

const Registratura: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState<'intrare' | 'iesire'>('intrare');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentIntrare | DocumentIesire | null>(null);

  // Mock data pentru documente intrare
  const [documentsIntrare] = useState<DocumentIntrare[]>([
    {
      id: '1',
      nrInregistrare: 'INT-001/2024',
      dataInregistrare: { anul: 2024, luna: 1, ziua: 15 },
      nrSiAdresaDocument: 'CER-2024-001, Parohia Sf. Nicolae, Via Roma 123, Milano',
      emitent: 'Parohia Sf. Nicolae',
      continutDocument: 'Cerere de căsătorie pentru credincioșii Ion și Maria',
      compartimentPrimeste: 'Oficiul Canonic',
      status: 'inregistrat',
      prioritate: 'normal',
      parohieId: 'parohia1'
    },
    {
      id: '2',
      nrInregistrare: 'INT-002/2024',
      dataInregistrare: { anul: 2024, luna: 1, ziua: 16 },
      nrSiAdresaDocument: 'RAP-2024-001, Parohia Sf. Gheorghe, Corso Italia 45, Roma',
      emitent: 'Parohia Sf. Gheorghe',
      continutDocument: 'Raport financiar trimestrial - Q4 2023',
      compartimentPrimeste: 'Administrația Control Financiar',
      status: 'distribuit',
      prioritate: 'urgent',
      parohieId: 'parohia2'
    },
    {
      id: '3',
      nrInregistrare: 'INT-003/2024',
      dataInregistrare: { anul: 2024, luna: 1, ziua: 17 },
      nrSiAdresaDocument: 'NOT-2024-001, Parohia Sf. Maria, Piazza Duomo 12, Torino',
      emitent: 'Parohia Sf. Maria',
      continutDocument: 'Notificare despre programarea vizitei pastorale',
      compartimentPrimeste: 'Sectorul Pastoral Liturgic',
      status: 'arhivat',
      prioritate: 'normal',
      parohieId: 'parohia3'
    }
  ]);

  // Mock data pentru documente ieșire
  const [documentsIesire] = useState<DocumentIesire[]>([
    {
      id: '1',
      dataExpediere: { anul: 2024, luna: 1, ziua: 18 },
      destinatar: 'Parohia Sf. Nicolae, Via Roma 123, Milano',
      continutDocument: 'Decizie privind aprobarea cererii de căsătorie',
      nrInregistrareAnexa: 'INT-001/2024',
      indicativDosar: 'DOS-CAS-2024-001',
      status: 'expediat',
      prioritate: 'normal',
      parohieId: 'parohia1'
    },
    {
      id: '2',
      dataExpediere: { anul: 2024, luna: 1, ziua: 19 },
      destinatar: 'Parohia Sf. Gheorghe, Corso Italia 45, Roma',
      continutDocument: 'Circulară privind organizarea sărbătorilor de Paște',
      nrInregistrareAnexa: 'INT-002/2024',
      indicativDosar: 'DOS-PASTE-2024-001',
      status: 'confirmat',
      prioritate: 'urgent',
      parohieId: 'parohia2'
    },
    {
      id: '3',
      dataExpediere: { anul: 2024, luna: 1, ziua: 20 },
      destinatar: 'Parohia Sf. Maria, Piazza Duomo 12, Torino',
      continutDocument: 'Confirmare vizită pastorală - 25 ianuarie 2024',
      nrInregistrareAnexa: 'INT-003/2024',
      indicativDosar: 'DOS-VIZ-2024-001',
      status: 'pregatit',
      prioritate: 'normal',
      parohieId: 'parohia3'
    }
  ]);

  const statusOptionsIntrare = ['inregistrat', 'distribuit', 'arhivat'];
  const statusOptionsIesire = ['pregatit', 'expediat', 'confirmat'];

  const filteredDocumentsIntrare = documentsIntrare.filter(doc => {
    const matchesSearch = doc.continutDocument.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.nrInregistrare.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.emitent.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || doc.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const filteredDocumentsIesire = documentsIesire.filter(doc => {
    const matchesSearch = doc.continutDocument.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.destinatar.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || doc.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string, type: 'intrare' | 'iesire') => {
    if (type === 'intrare') {
      switch (status) {
        case 'inregistrat': return 'warning';
        case 'distribuit': return 'info';
        case 'arhivat': return 'success';
        default: return 'default';
      }
    } else {
      switch (status) {
        case 'pregatit': return 'warning';
        case 'expediat': return 'info';
        case 'confirmat': return 'success';
        default: return 'default';
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'normal': return 'default';
      case 'urgent': return 'warning';
      case 'foarte_urgent': return 'error';
      default: return 'default';
    }
  };

  const handleViewDocument = (document: DocumentIntrare | DocumentIesire) => {
    setSelectedDocument(document);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDocument(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Registratura
        </Typography>
        {hasPermission('registratura.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare document nou */}}
          >
            Document nou
          </Button>
        )}
      </Box>

      {/* Tab-uri pentru Intrare și Ieșire */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex' }}>
            <Button
              variant={activeTab === 'intrare' ? 'contained' : 'text'}
              onClick={() => setActiveTab('intrare')}
              sx={{ flex: 1, py: 2 }}
            >
              INTRARE
            </Button>
            <Button
              variant={activeTab === 'iesire' ? 'contained' : 'text'}
              onClick={() => setActiveTab('iesire')}
              sx={{ flex: 1, py: 2 }}
            >
              IEȘIRE
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filtre și căutare */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={activeTab === 'intrare' ? 'Caută documente intrare...' : 'Caută documente ieșire...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                {(activeTab === 'intrare' ? statusOptionsIntrare : statusOptionsIesire).map(status => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistici rapide */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {activeTab === 'intrare' ? (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total intrare
                  </Typography>
                  <Typography variant="h5">
                    {documentsIntrare.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Înregistrate
                  </Typography>
                  <Typography variant="h5">
                    {documentsIntrare.filter(d => d.status === 'inregistrat').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Distribuite
                  </Typography>
                  <Typography variant="h5">
                    {documentsIntrare.filter(d => d.status === 'distribuit').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Arhivate
                  </Typography>
                  <Typography variant="h5">
                    {documentsIntrare.filter(d => d.status === 'arhivat').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total ieșire
                  </Typography>
                  <Typography variant="h5">
                    {documentsIesire.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Pregătite
                  </Typography>
                  <Typography variant="h5">
                    {documentsIesire.filter(d => d.status === 'pregatit').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Expediate
                  </Typography>
                  <Typography variant="h5">
                    {documentsIesire.filter(d => d.status === 'expediat').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Confirmate
                  </Typography>
                  <Typography variant="h5">
                    {documentsIesire.filter(d => d.status === 'confirmat').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Tabel documente */}
      <TableContainer component={Paper}>
        <Table>
          {activeTab === 'intrare' ? (
            <>
              <TableHead>
                <TableRow>
                  <TableCell>Nr. de înregistrare</TableCell>
                  <TableCell>Data înregistrării</TableCell>
                  <TableCell>Nr. și adresa documentului</TableCell>
                  <TableCell>Emitent</TableCell>
                  <TableCell>Conținutul pe scurt al documentului</TableCell>
                  <TableCell>Compartiment care primește corespondența</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocumentsIntrare.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>{document.nrInregistrare}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {document.dataInregistrare.ziua}/{document.dataInregistrare.luna}/{document.dataInregistrare.anul}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {document.nrSiAdresaDocument}
                      </Typography>
                    </TableCell>
                    <TableCell>{document.emitent}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 250 }}>
                        {document.continutDocument}
                      </Typography>
                    </TableCell>
                    <TableCell>{document.compartimentPrimeste}</TableCell>
                    <TableCell>
                      <Chip
                        label={document.status}
                        color={getStatusColor(document.status, 'intrare') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewDocument(document)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('registratura.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <>
              <TableHead>
                <TableRow>
                  <TableCell>Data expedierii</TableCell>
                  <TableCell>Destinatar</TableCell>
                  <TableCell>Conținutul pe scurt al documentului</TableCell>
                  <TableCell>Nr. de înregistrare la care se anexează</TableCell>
                  <TableCell>Indicativul dosarului</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocumentsIesire.map((document) => (
                  <TableRow key={document.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {document.dataExpediere.ziua}/{document.dataExpediere.luna}/{document.dataExpediere.anul}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {document.destinatar}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 250 }}>
                        {document.continutDocument}
                      </Typography>
                    </TableCell>
                    <TableCell>{document.nrInregistrareAnexa}</TableCell>
                    <TableCell>{document.indicativDosar}</TableCell>
                    <TableCell>
                      <Chip
                        label={document.status}
                        color={getStatusColor(document.status, 'iesire') as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewDocument(document)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      {hasPermission('registratura.write') && (
                        <IconButton color="secondary">
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton color="default">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare document */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii document - {activeTab === 'intrare' ? 
            (selectedDocument as DocumentIntrare)?.nrInregistrare : 
            'IES-' + (selectedDocument as DocumentIesire)?.id
          }
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Grid container spacing={2}>
              {activeTab === 'intrare' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Număr înregistrare
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).nrInregistrare}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Data înregistrării
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).dataInregistrare.ziua}/
                      {(selectedDocument as DocumentIntrare).dataInregistrare.luna}/
                      {(selectedDocument as DocumentIntrare).dataInregistrare.anul}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Nr. și adresa documentului
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).nrSiAdresaDocument}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Emitent
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).emitent}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Compartiment care primește
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).compartimentPrimeste}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Conținutul pe scurt al documentului
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIntrare).continutDocument}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={(selectedDocument as DocumentIntrare).status}
                      color={getStatusColor((selectedDocument as DocumentIntrare).status, 'intrare') as any}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Data expedierii
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIesire).dataExpediere.ziua}/
                      {(selectedDocument as DocumentIesire).dataExpediere.luna}/
                      {(selectedDocument as DocumentIesire).dataExpediere.anul}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={(selectedDocument as DocumentIesire).status}
                      color={getStatusColor((selectedDocument as DocumentIesire).status, 'iesire') as any}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Destinatar
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIesire).destinatar}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Conținutul pe scurt al documentului
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIesire).continutDocument}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Nr. de înregistrare la care se anexează
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIesire).nrInregistrareAnexa}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Indicativul dosarului
                    </Typography>
                    <Typography variant="body1">
                      {(selectedDocument as DocumentIesire).indicativDosar}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Registratura;

